import React from 'react';
import { WithTranslation, withTranslation } from 'react-i18next';
import styled from 'styled-components/macro';

import { border, colors, distances, grid, transaction_status } from '../../../styles/constants';
import { DateTime, Money } from '../../../components/Formatters';
import { H2, H3, P } from '../../../components/Typography';
import { Transaction, TransactionEvent } from '../../../types/checkout';
import { Dd, Dl, Dt } from '../../../components/DescriptionList/Horizontal';
import { formatDate } from '../../../helpers/formatters';
import { TransactionSettlementsRead } from '../../../types/checkout/generated';
import { Link, RouteComponentProps, withRouter } from 'react-router-dom';
import Hr from '../../../components/Hr';
interface MatchParams {
    accountId: string;
}

export interface TransactionHistoryProps extends WithTranslation, RouteComponentProps<MatchParams> {
    transaction: Transaction;
    openEventHistoryDetailsModal: (details: any) => void;
}

const getStatusTitle = (t: any, event: TransactionEvent, reconstructedBy: TransactionEvent | false) => {
    if (event.event && !event.success) {
        return t('payments.transaction.operation_failed', {
            operation: t(`payments.transaction_event_operations.${event.event}`),
        });
    }
    if (event.event === 'INITIATE_REFUND') {
        return t(`payments.transaction_statuses.${event.event}`);
    }
    if (event.event === 'INITIATE_CAPTURE') {
        return t(`payments.transaction_statuses.${event.event}`);
    }
    if (event.event === 'SETTLEMENT') {
        return t(`payments.transaction_statuses.${event.settlements?.settlement_status ?? 'unknown'}`);
    }
    if (
        event.event === 'VOID' &&
        [
            'CAPTURED',
            'REFUNDED',
            'PARTIALLY_CAPTURED',
            'PARTIALLY_CAPTURED_REFUNDED',
            'PARTIALLY_REFUNDED',
            'PARTIALLY_SETTLED',
            'SETTLED',
            'SETTLEMENT_COMPLETED',
        ].includes(event.transaction_status || '')
    ) {
        return t(`payments.transaction_statuses.VOID_AFTER_CAPTURE`);
    }
    if (reconstructedBy) {
        return t(`payments.transaction_event_operations.${event.event}`);
    }
    return t(`payments.transaction_statuses.${event.transaction_status}`);
};

const turnCorrectionsIntoStandaloneEvents = (events: TransactionEvent[]): TransactionEvent[] => {
    return events.reduce<TransactionEvent[]>((acc, event) => {
        if (event.correction) {
            const errorEvent: TransactionEvent = { ...event };
            delete errorEvent.correction;
            const correctionEvent = {
                id: errorEvent.id,
                transaction_status: event.correction.status,
                correction: event.correction,
                created_at: event.created_at,
                created_by: event.created_by,
            } as TransactionEvent;
            return [...acc, errorEvent, correctionEvent];
        }
        return [...acc, event];
    }, []);
};

type SettlementFields = 'amount' | 'capture' | 'refund' | 'fee';
const settlementAmount = (settlements: TransactionSettlementsRead | undefined, field: SettlementFields) => {
    if (!settlements) {
        return 0;
    }
    return settlements.events.reduce((acc, item) => acc + (item[field] || 0), 0);
};

const correctionReconstruction = (event: TransactionEvent, events: TransactionEvent[]) => {
    const transactionCorrectionEvents = events.filter(e => e.correction);
    const reconstructedBy = transactionCorrectionEvents.find(ce => (ce.correction?.event_ids || []).includes(event.id || ''));
    return reconstructedBy || false;
};

const TransactionHistory = (
    {
        i18n,
        t,
        transaction,
        match,
        openEventHistoryDetailsModal,
    }: TransactionHistoryProps
) => {
    const lng = i18n.language;
    const events: TransactionEvent[] = turnCorrectionsIntoStandaloneEvents(transaction.events || []);
    return (
        <Wrapper>
            <H2>{t('payments.transaction.transaction_history')}</H2>
            <Hr />
            <div>
                {events.map((event, i) => {
                    const collectorInvoiceUrl = metadataFieldFromEvent(event, 'collector:Invoice.InvoiceUrl');
                    const collectorInvoiceNumber = metadataFieldFromEvent(
                        event,
                        'collector:Invoice.CurrentInvoiceNumber'
                    );
                    const collectorInvoiceDueDate = metadataFieldFromEvent(event, 'collector:Invoice.DueDate');
                    const collectorStatus = metadataFieldFromEvent(event, 'collector:Invoice.InvoiceStatus');
                    const vippsTxnId = metadataFieldFromEvent(event, 'vipps:transactionInfo.transactionId');
                    const swishId = metadataFieldFromEvent(event, 'swish:id');
                    const swishPaymentReference = metadataFieldFromEvent(event, 'swish:paymentReference');
                    const reconstructedBy = correctionReconstruction(event, events);
                    return (
                        <EventWrapper key={i} onClick={() => openEventHistoryDetailsModal(event)}>
                            {event.error || event.success === false ? (
                                <Dot status={'FAILED'} className={i === events.length - 1 ? 'active dot' : 'dot'} />
                            ) : (
                                <Dot
                                    status={event.transaction_status || ''}
                                    className={i === events.length - 1 ? 'active dot' : 'dot'}
                                />
                            )}
                            <H3>{getStatusTitle(t, event, reconstructedBy)} </H3>
                            <DateTimeWrapper>
                                <DateTime isoString={event.created_at || ''} boldTime />
                            </DateTimeWrapper>

                            <Fields>
                                {'amount' in event && (
                                    <Field>
                                        <Money amount={event.amount} currency={transaction.currency} />
                                    </Field>
                                )}
                                {event.event === 'SETTLEMENT' && event.settlements && (
                                    <React.Fragment>
                                        {['amount', 'capture', 'refund', 'fee'].map((field) => {
                                            return (
                                                <Field key={`field-${field}`}>
                                                    <HistoryDl>
                                                        <HistoryDt>
                                                            {t(`payments.transaction.settlements.${field}`)}:
                                                        </HistoryDt>
                                                        <HistoryDd>
                                                            <Money
                                                                amount={settlementAmount(
                                                                    event.settlements,
                                                                    field as SettlementFields
                                                                )}
                                                                currency={transaction.currency}
                                                            />
                                                        </HistoryDd>
                                                    </HistoryDl>
                                                </Field>
                                            );
                                        })}
                                        {event.settlements.events.map((settlement, i) => {
                                            return (
                                                <Field key={`settlement-${i}-${settlement.provider_reference}`}>
                                                    <HistoryDl>
                                                        <HistoryDt>
                                                            {t('payments.transaction.settlements.settlement_reference')}
                                                            :
                                                        </HistoryDt>
                                                        <HistoryDd noTruncate>
                                                            <Link title={settlement.provider_reference}
                                                                to={`/${match.params.accountId}/reports/settlements?search=${settlement.provider_reference}`}
                                                            >
                                                                {settlement.provider_reference}
                                                            </Link>
                                                        </HistoryDd>
                                                    </HistoryDl>
                                                </Field>
                                            );
                                        })}
                                    </React.Fragment>
                                )}
                                {event.metadata && collectorStatus && (
                                    <Field>
                                        <HistoryDl>
                                            <HistoryDt>
                                                {t('payments.transaction.collector_invoice_status.title')}:
                                            </HistoryDt>
                                            <HistoryDd>
                                                {t(
                                                    `payments.transaction.collector_invoice_status.description.${collectorStatus}`
                                                )}
                                            </HistoryDd>
                                        </HistoryDl>
                                    </Field>
                                )}
                                {event.metadata && collectorInvoiceNumber && (
                                    <Field>
                                        <HistoryDl>
                                            <HistoryDt>
                                                {t('payments.transaction.collector_invoice.invoice_number.title')}:
                                            </HistoryDt>
                                            <HistoryDd>{collectorInvoiceNumber}</HistoryDd>
                                        </HistoryDl>
                                    </Field>
                                )}
                                {event.metadata && collectorInvoiceDueDate && (
                                    <Field>
                                        <HistoryDl>
                                            <HistoryDt>
                                                {t('payments.transaction.collector_invoice.due_date.title')}:
                                            </HistoryDt>
                                            <HistoryDd>{formatDate(collectorInvoiceDueDate, lng)}</HistoryDd>
                                        </HistoryDl>
                                    </Field>
                                )}
                                {event.metadata && collectorInvoiceUrl && (
                                    <Field>
                                        <HistoryDl>
                                            <HistoryDd>
                                                <a href={collectorInvoiceUrl} target="_blank" rel="noopener noreferrer">
                                                    {t(`payments.transaction.collector_invoice.url.description`)}
                                                </a>
                                            </HistoryDd>
                                        </HistoryDl>
                                    </Field>
                                )}
                                {event.metadata && vippsTxnId && (
                                    <Field>
                                        <HistoryDl>
                                            <HistoryDt>{t('payments.transaction.vipps.transaction_id')}:</HistoryDt>
                                            <HistoryDd>{vippsTxnId}</HistoryDd>
                                        </HistoryDl>
                                    </Field>
                                )}
                                {event.metadata && swishId && (
                                    <Field>
                                        <HistoryDl>
                                            <HistoryDt>{t('payments.transaction.swish.id')}:</HistoryDt>
                                            <HistoryDd>{swishId}</HistoryDd>
                                        </HistoryDl>
                                    </Field>
                                )}
                                {event.metadata && swishPaymentReference && (
                                    <Field>
                                        <HistoryDl>
                                            <HistoryDt>{t('payments.transaction.swish.payment_reference')}:</HistoryDt>
                                            <HistoryDd>{swishPaymentReference}</HistoryDd>
                                        </HistoryDl>
                                    </Field>
                                )}
                                {event.correction && (
                                    <>
                                        <br />
                                        <Field>
                                            <strong>{t('payments.transaction.transaction_correction')}</strong>
                                        </Field>
                                        <Field>
                                            {t('payments.transaction.remaining_capture_amount')}{' '}
                                            <Money
                                                amount={event.correction.remaining_capture_amount}
                                                currency={transaction.currency}
                                            />
                                        </Field>
                                        <Field>
                                            {t('payments.transaction.remaining_refund_amount')}{' '}
                                            <Money
                                                amount={event.correction.remaining_refund_amount}
                                                currency={transaction.currency}
                                            />
                                        </Field>
                                        <Field>
                                            {t('payments.transaction.remaining_void_amount')}{' '}
                                            <Money
                                                amount={event.correction.remaining_void_amount}
                                                currency={transaction.currency}
                                            />
                                        </Field>
                                    </>
                                )}
                                {reconstructedBy && <Field>
                                    {t('payments.transaction.reconstructed_by')}
                                </Field>}
                                {event.request_headers && (
                                    <>
                                        {([
                                            'dintero-system-name',
                                            'dintero-system-version',
                                            'dintero-system-plugin-name',
                                            'dintero-system-plugin-version',
                                        ] as const).map((field, j) => {
                                            const value =
                                                event && event.request_headers && event.request_headers[field];
                                            if (!value) {
                                                return undefined;
                                            }
                                            return (
                                                <Field key={i + '-' + j}>
                                                    <HistoryDl>
                                                        <HistoryDt>{t(`request_headers.${field}`)}:</HistoryDt>
                                                        <HistoryDd title={value}>{value}</HistoryDd>
                                                    </HistoryDl>
                                                </Field>
                                            );
                                        })}
                                    </>
                                )}
                            </Fields>
                        </EventWrapper>
                    );
                })}
            </div>
        </Wrapper>
    );
};

export default withRouter(withTranslation()(TransactionHistory));

const Wrapper = styled.div`
    padding-top: ${distances.normal};
    max-width: ${grid.spans.span2};
    width: 100%;

    @media (max-width: 1200px) {
        max-width: 100%;
    }
`;



interface DotProps {
    status: string;
}

const Dot = styled.div<DotProps>`
    position: absolute;
    top: 5px;
    left: -8px;
    height: 20px;
    width: 20px;
    border-radius: 20px;
    background: rgba(242, 242, 242, 1);

    &:after {
        content: ' ';
        position: absolute;
        top: 3px;
        left: 3px;
        height: 14px;
        width: 14px;
        border-radius: 18px;
        background: ${(props) => transaction_status[props.status].border || 'transparent'};
        opacity: 0;
        filter: saturate(200%);
        box-shadow: 0 0 3px rgba(0,0,0,0, 0.75);
    }
    &.active:after {
        opacity: 1;
    }
`;

const EventWrapper = styled.div`
    padding-top: 0;
    padding-left: ${distances.small};
    padding-bottom: 22px;
    width: 100%;
    font-size: 11px;
    position: relative;
    color: #bdc2c5;

    a {
        color: #bdc2c5;
    }

    &:before {
        content: ' ';
        position: absolute;
        top: 0;
        bottom: 0;
        left: 0;
        width: 4px;
        background: rgba(242, 242, 242, 1);
    }

    &:last-child {
        color: #46535e;

        a {
            color: #46535e;
        }
    }

    &:first-child:before {
        content: ' ';
        position: absolute;
        top: ${distances.small};
        left: 0;
        bottom: 0;
        width: 4px;
        background: rgba(242, 242, 242, 1);
    }

    &:first-child:last-child:before {
        display: none;
    }

    &:hover {
        color: #46535e;

        > .dot:after {
            opacity: 0.5;
        }

        > .dot.active:after {
            opacity: 1;
        }

        a {
            color: #46535e;
        }
    }

    &:last-child:before {
        content: ' ';
        position: absolute;
        top: 0;
        height: 10px;
        left: 0;
        width: 4px;
        background: rgba(242, 242, 242, 1);
    }
`;

const Fields = styled.span`
    display: block;
    line-height: ${distances.small};
    font-feature-settings: 'tnum';
`;

const Field = styled.span`
    display: block;
    word-break: normal;
    white-space: nowrap;
`;

const DateTimeWrapper = styled.div`
    font-weight: 400;
    line-height: ${distances.small};
    font-feature-settings: 'tnum';
`;

const HistoryDl = styled(Dl)`
    &:hover {
        border-bottom: 1px solid transparent;

    }
`;

const HistoryDt = styled(Dt)`
    font-weight: inherit;
    font-size: inherit;
    line-height: inherit;
`;

const HistoryDd = styled(Dd) <{
    noTruncate?: boolean;
}>`
    font-size: inherit;
    line-height: inherit;
    ${props => props.noTruncate ? 'text-overflow: unset; overflow: unset;' : 'text-overflow: ellipsis;'}
`;

const metadataFieldFromEvent = (evt: TransactionEvent, field: string) => {
    const metadata = evt.metadata as any;
    if (!metadata) {
        return undefined;
    }
    return metadata?.[field];
};
